/**
 * Keyboard binding defaults — the ONE definition of which physical keys drive
 * the semantic signals. The shell folds these into input frames; the studio
 * remap editor shows and edits them; `ControlsConfig.keys` overrides the
 * merged cluster per creation.
 */
import type { KeyBindings, SemanticKey } from './types.js';

/** The three seat clusters (documented seat → keys contract):
 *  `merged` — the single local seat of solo/vsBot/onlineVersus: arrows AND
 *  WASD both steer, Space acts (and it is the ONLY remappable cluster);
 *  `p1`/`p2` — the two localVersus seats (WASD+Space vs arrows+Enter). */
export const DEFAULT_CLUSTERS: Readonly<Record<'merged' | 'p1' | 'p2', KeyBindings>> = {
  merged: {
    up: ['ArrowUp', 'KeyW'],
    down: ['ArrowDown', 'KeyS'],
    left: ['ArrowLeft', 'KeyA'],
    right: ['ArrowRight', 'KeyD'],
    action: ['Space'],
  },
  p1: {
    up: ['KeyW'],
    down: ['KeyS'],
    left: ['KeyA'],
    right: ['KeyD'],
    action: ['Space'],
  },
  p2: {
    up: ['ArrowUp'],
    down: ['ArrowDown'],
    left: ['ArrowLeft'],
    right: ['ArrowRight'],
    action: ['Enter'],
  },
};

/** The merged cluster with a creator's sparse overrides applied per signal. */
export function mergedBindings(overrides?: Partial<KeyBindings>): KeyBindings {
  if (!overrides) return DEFAULT_CLUSTERS.merged;
  const out = { ...DEFAULT_CLUSTERS.merged } as Record<SemanticKey, readonly string[]>;
  for (const key of Object.keys(out) as SemanticKey[]) {
    const codes = overrides[key];
    if (codes && codes.length > 0) out[key] = codes;
  }
  return out;
}

/** Short human label for a `KeyboardEvent.code` keycap ('KeyW' → 'W',
 *  'ArrowUp' → 'UP', 'Digit3' → '3', 'Space' → 'SPACE'). Unknown shapes pass
 *  through unchanged. */
export function keyCodeLabel(code: string): string {
  const key = /^Key([A-Z])$/.exec(code);
  if (key?.[1]) return key[1];
  const digit = /^Digit([0-9])$/.exec(code);
  if (digit?.[1]) return digit[1];
  const arrow = /^Arrow(Up|Down|Left|Right)$/.exec(code);
  if (arrow?.[1]) return arrow[1].toUpperCase();
  if (code === 'ShiftLeft' || code === 'ShiftRight') return 'SHIFT';
  if (code === 'ControlLeft' || code === 'ControlRight') return 'CTRL';
  return code.toUpperCase();
}
